import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Tooltip,
  Paper,
  TextInput,
  Button,
  ScrollArea,
  Group,
  Text,
  Box,
  Loader,
  ThemeIcon,
  Select
} from '@mantine/core';
import { useSubscription } from '../contexts/SubscriptionContext';
import { personasAPI } from '../services/api';
import axios from 'axios';

const FloatingChatBubble = () => {
  const navigate = useNavigate();
  const { isProUser } = useSubscription();
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [contextualQuestions, setContextualQuestions] = useState([]);
  const scrollAreaRef = useRef(null);

  // ALL hooks must be called before any conditional returns
  useEffect(() => {
    // Load personas on component mount only for pro users
    if (!isProUser) return;

    const loadPersonas = async () => {
      try {
        const response = await personasAPI.getAll();
        setPersonas(response.data);
      } catch (error) {
        console.error('Failed to load personas:', error);
      }
    };
    loadPersonas();
  }, [isProUser]);

  useEffect(() => {
    if (isExpanded && !sessionId) {
      // Initialize session when expanded
      const newSessionId = `floating-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);

      // Load any existing conversation or add welcome message
      const savedMessages = localStorage.getItem('aiConciergeMessages');
      if (savedMessages) {
        setMessages(JSON.parse(savedMessages));
      } else {
        setMessages([{
          role: 'assistant',
          content: "👋 Hi! I'm here to help with gift ideas anytime. What are you looking for?",
          timestamp: new Date()
        }]);
      }
    }
  }, [isExpanded, sessionId]);

  useEffect(() => {
    // Save messages to localStorage for persistence (shared with full concierge)
    if (messages.length > 0) {
      localStorage.setItem('aiConciergeMessages', JSON.stringify(messages));
    }
  }, [messages]);

  // Generate contextual questions based on selected persona
  useEffect(() => {
    if (!selectedPersona) {
      setContextualQuestions([
        { label: '🎁 General gift ideas', context: 'I need help finding gift ideas' },
        { label: '🎂 Birthday gifts', context: 'Help me find birthday gift ideas' },
        { label: '💝 Romantic gifts', context: 'Suggest romantic gift ideas' },
        { label: '🎄 Holiday gifts', context: 'Help me find holiday gift suggestions' }
      ]);
      return;
    }

    const persona = personas.find(p => p.id === selectedPersona);
    if (!persona) return;

    const questions = [];
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const currentDate = new Date();

    // Age-based suggestions
    if (persona.age) {
      if (persona.age < 12) {
        questions.push({ label: '🧸 Kids toys & games', context: `Gift ideas for a ${persona.age}-year-old child` });
      } else if (persona.age < 25) {
        questions.push({ label: '🎓 Student gifts', context: `Gift ideas for a ${persona.age}-year-old young adult` });
      } else if (persona.age > 60) {
        questions.push({ label: '👴 Comfort gifts', context: `Gift ideas for a ${persona.age}-year-old senior` });
      }
    }

    // Relationship-based suggestions
    if (persona.relationship) {
      const relationship = persona.relationship.toLowerCase();
      if (relationship.includes('family') || relationship.includes('parent')) {
        questions.push({ label: '👨‍👩‍👧 Family bonding', context: `Gift ideas for family member - ${persona.relationship}` });
      } else if (relationship.includes('friend') || relationship.includes('best friend')) {
        questions.push({ label: '🤝 Friendship gifts', context: `Gift ideas for friend - ${persona.relationship}` });
      } else if (relationship.includes('colleague') || relationship.includes('coworker')) {
        questions.push({ label: '💼 Professional gifts', context: `Gift ideas for colleague - ${persona.relationship}` });
      } else if (relationship.includes('partner') || relationship.includes('spouse')) {
        questions.push({ label: '💕 Romantic gifts', context: `Gift ideas for romantic partner - ${persona.relationship}` });
      }
    }

    // Holiday-based suggestions (current season)
    if (currentMonth >= 11 || currentMonth <= 1) { // Winter holidays
      // Only add general Christmas if favorite holiday is not Christmas
      if (!persona.favorite_holiday || persona.favorite_holiday.toLowerCase() !== 'christmas') {
        questions.push({ label: '🎄 Christmas gifts', context: `Christmas gift ideas for ${persona.name}` });
      }
      if (persona.favorite_holiday) {
        questions.push({ label: `🎁 ${persona.favorite_holiday} gifts`, context: `${persona.favorite_holiday} gift ideas for ${persona.name}` });
      }
    } else if (currentMonth >= 5 && currentMonth <= 8) { // Summer
      questions.push({ label: '🏖️ Summer activities', context: `Summer gift ideas for ${persona.name}` });
    } else if (currentMonth >= 9 && currentMonth <= 10) { // Fall
      questions.push({ label: '🍂 Autumn gifts', context: `Fall/autumn gift ideas for ${persona.name}` });
    }

    // Birthday suggestions (general since we don't have specific dates)
    questions.push({ label: '🎂 Birthday surprises', context: `Birthday gift ideas for ${persona.name}` });

    // Favorite holiday if available
    if (persona.favorite_holiday) {
      questions.push({ label: `🎉 ${persona.favorite_holiday}`, context: `${persona.favorite_holiday} gift ideas for ${persona.name}` });
    }

    // Add some general personalized suggestions
    questions.push({ label: '🎁 Personalized gifts', context: `Personalized gift ideas for ${persona.name}` });
    questions.push({ label: '📚 Hobby gifts', context: `Gift ideas based on ${persona.name}'s interests` });

    // Limit to 6 questions for space
    setContextualQuestions(questions.slice(0, 6));
  }, [selectedPersona, personas]);

  // Only show for PRO users - now after all hooks are called
  if (!isProUser) {
    return null;
  }

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post('/ai-concierge/chat', {
        message: inputMessage,
        personaId: selectedPersona,
        conversationHistory: messages.slice(-5).map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        sessionId: sessionId
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.message,
        suggestions: response.data.suggestions,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or visit the full AI Concierge page.',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const openFullConcierge = () => {
    navigate('/ai-concierge');
  };

  const clearChat = () => {
    setMessages([{
      role: 'assistant',
      content: "👋 Chat cleared! How can I help with gifts today?",
      timestamp: new Date()
    }]);
    localStorage.removeItem('aiConciergeMessages');
  };

  if (!isExpanded) {
    return (
      <Tooltip label="AI Gift Concierge" position="left">
        <ActionIcon
          size="xl"
          radius="xl"
          variant="gradient"
          gradient={{ from: 'violet', to: 'grape' }}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1000,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onClick={toggleExpanded}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
          }}
        >
          <span style={{ fontSize: '24px', lineHeight: 1 }}>💬</span>
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Paper
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '350px',
        height: '500px',
        zIndex: 1000,
        borderRadius: '16px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        border: '2px solid rgba(109, 40, 217, 0.3)',
        background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.05) 0%, rgba(0,0,0,0.95) 100%)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        p="sm"
        style={{
          background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)',
          borderBottom: '1px solid rgba(109, 40, 217, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Group gap="xs">
          <ThemeIcon size="sm" radius="xl" variant="gradient" gradient={{ from: 'violet', to: 'purple' }}>
            🤖
          </ThemeIcon>
          <Text size="sm" fw={600} c="white">AI Concierge</Text>
        </Group>
        <Group gap="xs">
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={clearChat}>
            🗑️
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={openFullConcierge}>
            ↗️
          </ActionIcon>
          <ActionIcon size="sm" variant="subtle" color="gray" onClick={toggleExpanded}>
            ✕
          </ActionIcon>
        </Group>
      </Box>

      {/* Persona Selector */}
      <Box p="xs" style={{ borderBottom: '1px solid rgba(109, 40, 217, 0.3)' }}>
        <Select
          placeholder="Select persona (optional)"
          data={personas.map(p => ({ value: p.id.toString(), label: p.name }))}
          value={selectedPersona?.toString()}
          onChange={(value) => setSelectedPersona(value ? parseInt(value) : null)}
          clearable
          size="xs"
          radius="md"
          leftSection="🎭"
          styles={{
            input: {
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(109, 40, 217, 0.3)',
              color: 'white'
            },
            dropdown: {
              background: 'rgba(0, 0, 0, 0.9)',
              border: '1px solid rgba(109, 40, 217, 0.3)',
              zIndex: 2000
            }
          }}
        />
      </Box>

      {/* Contextual Questions */}
      {contextualQuestions.length > 0 && (
        <Box p="xs" style={{ borderBottom: '1px solid rgba(109, 40, 217, 0.3)' }}>
          <Text size="xs" fw={600} c="dimmed" mb="xs">💡 Quick Ideas:</Text>
          <Group gap="xs" wrap="wrap">
            {contextualQuestions.slice(0, 3).map((question, idx) => (
              <Button
                key={idx}
                size="xs"
                variant="light"
                color="violet"
                radius="xl"
                onClick={() => {
                  setInputMessage(question.context);
                  sendMessage(question.context);
                }}
                disabled={isLoading}
                style={{ fontSize: '10px', padding: '4px 8px', height: 'auto' }}
              >
                {question.label}
              </Button>
            ))}
          </Group>
        </Box>
      )}

      {/* Messages */}
      <ScrollArea style={{ flex: 1 }} viewportRef={scrollAreaRef}>
        <Box p="sm">
          {messages.map((message, idx) => (
            <Box
              key={idx}
              style={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '8px'
              }}
            >
              <Paper
                p="xs"
                radius="md"
                style={{
                  maxWidth: '80%',
                  background: message.role === 'user'
                    ? 'linear-gradient(135deg, rgba(25,113,194,0.3) 0%, rgba(21,170,191,0.2) 100%)'
                    : 'linear-gradient(135deg, rgba(109, 40, 217, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)',
                  border: message.role === 'user'
                    ? '1px solid rgba(25,113,194,0.4)'
                    : '1px solid rgba(109, 40, 217, 0.3)'
                }}
              >
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                  {message.content}
                </Text>
              </Paper>
            </Box>
          ))}
          {isLoading && (
            <Box style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
              <Paper
                p="xs"
                radius="md"
                style={{
                  background: 'linear-gradient(135deg, rgba(109, 40, 217, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)',
                  border: '1px solid rgba(109, 40, 217, 0.3)'
                }}
              >
                <Group gap="xs">
                  <Loader size="xs" />
                  <Text size="xs" c="dimmed">Thinking...</Text>
                </Group>
              </Paper>
            </Box>
          )}
        </Box>
      </ScrollArea>

      {/* Input */}
      <Box p="sm" style={{ borderTop: '1px solid rgba(109, 40, 217, 0.3)' }}>
        <Group gap="xs">
          <TextInput
            placeholder="Ask about gifts..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            disabled={isLoading}
            size="xs"
            radius="xl"
            style={{ flex: 1 }}
          />
          <Button
            size="xs"
            radius="xl"
            variant="gradient"
            gradient={{ from: 'violet', to: 'purple' }}
            onClick={sendMessage}
            disabled={isLoading || !inputMessage.trim()}
            style={{ minWidth: '40px', padding: '0 8px' }}
          >
            {isLoading ? <Loader size="xs" color="white" /> : '→'}
          </Button>
        </Group>
      </Box>
    </Paper>
  );
};

export default FloatingChatBubble;
